// src/pages/TermsPage.js
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, FileText, Database, CreditCard, Scale, User } from 'lucide-react';

import Config from "../Config.json";
import ICON from "../assets/icons/favicon.ico";

const TITLE = `${Config.SITE_TITLE} - Termos de Uso e Privacidade`;

function TermsPage() {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const Section = ({ icon: Icon, title, children }) => (
        <div className="mb-10">
            <h2 className="font-serif text-2xl text-[#528a89] flex items-center gap-3 mb-4 border-b border-[#dbcdac]/30 pb-2">
                <Icon size={24} className="text-[#dbcdac]" /> {title}
            </h2>
            <div className="text-stone-600 space-y-4 font-light leading-relaxed">
                {children}
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-[#FFF5EE] pt-24 pb-32 relative overflow-hidden font-sans text-stone-800">
            <title>{TITLE}</title>
            <link rel="icon" href={ICON} />

            {/* Textura Vintage */}
            <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/aged-paper.png')]" />

            <div className="max-w-4xl mx-auto px-4 md:px-6 relative z-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
                
                <button 
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-stone-400 hover:text-[#971253] font-bold uppercase tracking-widest text-xs mb-8 transition-colors group"
                >
                    <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" /> 
                    Voltar
                </button>

                <div className="text-center md:text-left mb-12">
                    <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#971253] mb-4">
                        Termos de Uso e Privacidade
                    </h1>
                    <p className="text-lg text-[#528a89] font-light">
                        Transparência e segurança na sua jornada pela Deli da Persy.
                    </p>
                    <p className="text-xs text-stone-400 uppercase tracking-widest mt-4">
                        Última atualização: Fevereiro de 2026
                    </p>
                </div>

                <div className="bg-white/80 backdrop-blur-sm rounded-[2rem] border border-[#dbcdac]/40 shadow-sm p-8 md:p-12">
                    
                    <Section icon={FileText} title="1. Aceitação dos Termos">
                        <p>
                            Ao acessar o sistema de reservas e assinaturas da <strong>{Config.SITE_TITLE}</strong>, você concorda em cumprir estes termos de serviço, todas as leis e regulamentos aplicáveis. Se você não concordar com algum destes termos, está proibido de usar ou acessar este sistema.
                        </p>
                    </Section>

                    <Section icon={Database} title="2. Coleta e Uso de Informações (LGPD)">
                        <p>
                            Para garantir a melhor experiência em nosso espaço, coletamos informações pessoais essenciais, como <strong>nome, e-mail e número de telefone (WhatsApp)</strong>. 
                        </p>
                        <p>
                            <strong>O que fazemos com seus dados:</strong>
                        </p>
                        <ul className="list-disc pl-5 space-y-2 text-stone-600">
                            <li>Gerenciamento de reservas de mesas, controle de lotação e identificação de clientes no local.</li>
                            <li>Envio de notificações transacionais (confirmação de reserva, links de divisão de pagamento, alertas de tempo limite).</li>
                            <li>Comunicações de marketing, ofertas exclusivas e atualizações do Clube do Assinante (você pode optar por sair dessas comunicações a qualquer momento).</li>
                            <li>Análise de dados internos para melhorar nosso acervo de jogos e cardápio.</li>
                        </ul>
                    </Section>

                    <Section icon={ShieldCheck} title="3. Compartilhamento e Proteção de Dados">
                        <p>
                            Nós <strong>não vendemos, alugamos ou repassamos</strong> seus dados pessoais para terceiros com fins de lucro. 
                        </p>
                        <p>
                            Suas informações são compartilhadas estritamente com parceiros operacionais essenciais para o funcionamento da plataforma, tais como provedores de infraestrutura em nuvem (AWS) e plataformas de processamento de pagamentos. Todos os dados são trafegados de forma criptografada.
                        </p>
                    </Section>

                    <Section icon={CreditCard} title="4. Pagamentos, Assinaturas e Divisão de Contas">
                        <p>
                            <strong>Pagamentos:</strong> Todas as transações financeiras são processadas por intermediadores de pagamento seguros. A Deli da Persy não armazena os dados completos do seu cartão de crédito em seus servidores.
                        </p>
                        <p>
                            <strong>Divisão de Contas (Split):</strong> Ao utilizar a função de divisão de contas, cada participante receberá um link de pagamento individual. A mesa só é considerada "Confirmada" após a quitação de todos os links atrelados àquela reserva dentro do tempo limite estipulado.
                        </p>
                        <p>
                            <strong>Clube do Assinante:</strong> O plano de assinatura confere benefícios exclusivos aos titulares cadastrados. O compartilhamento do acesso com pessoas não cadastradas no plano constitui violação destes termos e pode resultar no cancelamento da assinatura sem reembolso.
                        </p>
                    </Section>

                    <Section icon={Scale} title="5. Cancelamentos e Reembolsos">
                        <p>
                            <strong>Reservas Diárias:</strong> Cancelamentos solicitados com antecedência mínima de 24 horas da data agendada garantem o reembolso integral ou reagendamento da mesa. O não comparecimento (No-Show) não dá direito a reembolso, visto que a mesa foi bloqueada para outros aventureiros.
                        </p>
                        <p>
                            <strong>Assinaturas:</strong> O cancelamento de assinaturas semestrais ou anuais interrompe a renovação automática para o próximo ciclo. Não efetuamos reembolsos proporcionais por meses parcialmente não utilizados.
                        </p>
                    </Section>

                    <Section icon={User} title="6. Seus Direitos">
                        <p>
                            Você tem o direito de solicitar, a qualquer momento, o acesso, a correção ou a exclusão total dos seus dados pessoais de nosso banco de dados. Para exercer esses direitos, basta entrar em contato através dos canais oficiais de atendimento da Deli da Persy.
                        </p>
                    </Section>

                </div>
            </div>
        </div>
    );
}

export default TermsPage;